// File: frontend/src/components/profile/MessageButton.tsx

import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./MessageButton.css";

interface MessageButtonProps {
  targetUserId: number;
  targetUsername: string;
  // Permanent role of the target user (ROOKIE/AUTHOR/MODERATOR/ADMIN).
  // Passed straight through to the messages page so the chat header can render
  // the role label before the conversation payload arrives.
  targetUserRole: string;
}

/**
 * Envelope icon rendered inline so the button carries no icon-library dependency.
 * Same glyph the online users list uses, kept visually consistent across the site.
 */
const MessageIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
  </svg>
);

/**
 * "Send message" icon button on the profile header.
 *
 * Navigates to the messages page carrying the recipient in router state, which is
 * the same contract the online users modal already uses: MessagesPage reads
 * { recipientId, recipientUsername, recipientRole } on mount and opens (or creates)
 * the conversation with that user.
 *
 * The caller is responsible for deciding whether this button should exist at all —
 * it is rendered only when the backend reports the viewer may message this user.
 */
const MessageButton: React.FC<MessageButtonProps> = ({
  targetUserId,
  targetUsername,
  targetUserRole,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate("/mesajlar", {
      state: {
        recipientId: targetUserId,
        recipientUsername: targetUsername,
        recipientRole: targetUserRole,
      },
    });
  };

  const label = t("online_send_message", { username: targetUsername });

  return (
    <button
      className="profile-message-btn"
      onClick={handleClick}
      type="button"
      title={label}
      aria-label={label}
    >
      <MessageIcon />
    </button>
  );
};

export default MessageButton;